import { defineStore } from 'pinia';
import { ref } from 'vue';
import jsonFetch from '@globals/jsonFetch';
import { useAppStore } from '@globals/stores/app';
import { HelloResponse } from '@globals/types/Hello';
import ServiceMessage from '@customer/types/ServiceMessage';
import * as mobileApp from '@customer/utilities/MobileApp';

export const useImportantMessageStore = defineStore('importantMessage', () => {
	const state = {
		message: ref<ServiceMessage | undefined>(undefined),
	};

	const actions = {
		/**
		 * Przetwarza ważny komunikat, np. o braku środków na koncie z odpowiedzi na `hello.ofer`.
		 * Jeżeli nie ma żadnych innych, sprawdź czy można wyświetlić komunikat o powiadomieniach Push
		 * @param rsvp Odpowiedź serwera
		 * @returns Obiekt komunikatu
		 */
		update(rsvp: HelloResponse) {
			const skipCheck = false;
			const displayServiceMessages = rsvp.user?.canShowImportantMessage;

			// Nie pokazuj żadnej wiadomości w tej sesji
			if (!displayServiceMessages && !skipCheck) {
				return;
			}

			// W innym przypadku...
			const msg = rsvp.user?.importantMessage;

			if (msg) {
				state.message.value = {
					id: msg.id,
					idUserMessageGroup: msg.idUserMessageGroup,
					type: msg.type || 'info',
					title: msg.title,
					message: msg.message.replace(/<br>/g, '').replace(/<br\/>/g, ''),
					button: msg.buttonLabel || 'Dowiedz się więcej',
					dismissButton: msg.dismissButtonLabel || 'Przypomnij później',
					action: msg.moreInfoLink,
				};
				return;
			}

			state.message.value = actions.getImportantAppMessage() || actions.getImportantPushMessage();
		},

		/**
		 * W przypadku, kiedy nie ma żadnych innych komunikatów z serwera,
		 * sprawdza, czy może zwrócić komunikat dotyczący pobrania aplikacji mobilnej.
		 */
		getImportantAppMessage(): ServiceMessage | undefined {
			const { customerViewAppMessageIgnored } = window.localStorage;
			const appType = mobileApp.canShowDownloadAppPrompt();

			const appStore = useAppStore();
			const hasApp = appStore.hasFlag('hasMobileApp');

			if (customerViewAppMessageIgnored || !appType) { return undefined; }

			if (hasApp) {
				window.localStorage.customerViewAppMessageIgnored = '';
				return undefined;
			}

			return {
				id: 'app',
				idUserMessageGroup: -1,
				type: 'info',
				title: 'Pobierz darmową aplikację Oferteo!',
				message: 'Odbieraj wiadomości od Zlecających w aplikacji',
				button: 'Pobierz',
				dismissButton: 'Przypomnij później',
				action: 'AppDownload',
			};
		},

		/**
		 * W przypadku, kiedy nie ma żadnych ważniejszych komunikatów,
		 * zwraca informację o możliwości włączenia powiadomień Push,
		 * o ile są spełnione stosowne warunki.
		 */
		getImportantPushMessage(): ServiceMessage | undefined {
			try {
				// Warunki do spełnienia:
				// - powiadomienia są obsługiwane przez przeglądarkę
				// - powiadomienia nie są wyłączone lub włączone
				// - komunikat nie został wyłączony więcej niż 3 razy
				// - komunikat nie został wyłączony podczas trwania tej sesji

				const appStore = useAppStore();
				const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
				const pushSupported = typeof Notification === 'function' && Notification.permission === 'default' && !isSafari;
				const { pushPromptDisabledCount, pushPromptDisabledSessionId } = window.localStorage;
				const { sessionId } = appStore;
				const pushPromptDisabled = Number(pushPromptDisabledCount || 0) > 3 || pushPromptDisabledSessionId === sessionId;

				if (pushSupported && !pushPromptDisabled) {
					return {
						id: 'push',
						idUserMessageGroup: -1,
						type: 'info',
						title: '',
						message: 'Włącz powiadomienia push o nowych zapytaniach i nie przegap żadnej okazji na zdobycie Klienta',
						button: 'Włącz',
						dismissButton: 'Przypomnij później',
						action: 'EnablePushNotifications',
					};
				}
			} catch (err) {
				console.warn('getImportantPushMessage', err);
			}

			return undefined;
		},

		/**
		 * Wyświetla informację analitycznę o zobaczeniu komunikatu.
		 */
		messageSeen() {
			if (!state.message.value) { return; }

			const { idUserMessageGroup } = state.message.value;
			const idMsgGrp = Number.isInteger(idUserMessageGroup) ? idUserMessageGroup : -1;
			jsonFetch(`/profile/messages/${idMsgGrp}/display-bar.do`, { method: 'POST' });
		},

		/**
		 * Ukrywa komunikat i wysyła informacje analityczne do serwera.
		 * @param {*} isSuccess Czy kliknięty został przycisk akcji?
		 */
		async hideMessage(isSuccess = true) {
			if (!state.message.value) { return; }

			const { idUserMessageGroup } = state.message.value;
			const idMsgGrp = Number.isInteger(idUserMessageGroup) ? idUserMessageGroup : -1;
			state.message.value = undefined;
			await jsonFetch(`/profile/messages/${idMsgGrp}/${isSuccess ? 'proceed' : 'discard'}.do`, { method: 'POST' });
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
